import { type ReactNode } from 'react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { TierRowReadonly } from '@/components/ui/pricing-tier-row-readonly';
import type { PricingRange } from '@/hooks/use-pricing-ranges';

type Props = {
  range: PricingRange;
  onEdit: () => void;
  onHistory: () => void;
  onDelete: () => void;
};

function rangeBounds(r: PricingRange): string {
  const min = r.range_min.toLocaleString('es-ES');
  const max = r.range_max == null ? '∞' : r.range_max.toLocaleString('es-ES');
  return `${min} – ${max}`;
}

function CardHeader({ range }: { range: PricingRange }): ReactNode {
  return (
    <div className="flex flex-wrap items-baseline gap-x-sm gap-y-1">
      <h3 className="text-sm font-semibold text-text-primary">
        {range.range_label || 'Sin etiqueta'}
      </h3>
      <span className="whitespace-nowrap text-xs tabular-nums text-text-tertiary">
        {rangeBounds(range)}
      </span>
      {range.is_current && (
        <span className="whitespace-nowrap text-[10px] font-semibold uppercase tracking-wider text-brand-red">
          Vigente
        </span>
      )}
    </div>
  );
}

type ActionsProps = {
  onEdit: () => void;
  onHistory: () => void;
  onDelete: () => void;
};

// Ghost variant gives borderless buttons; the hover override swaps the
// default text-text-primary for brand-red so the accent stays on-brand.
const ACTION_BTN = 'whitespace-nowrap hover:text-brand-red';

function ActionBtn(props: {
  onClick: () => void;
  children: ReactNode;
}): ReactNode {
  return (
    <Button
      size="sm"
      variant="ghost"
      onClick={props.onClick}
      className={ACTION_BTN}
    >
      {props.children}
    </Button>
  );
}

function CardActions(p: ActionsProps): ReactNode {
  return (
    <div className="mt-md flex items-center gap-sm">
      <ActionBtn onClick={p.onEdit}>Editar</ActionBtn>
      <ActionBtn onClick={p.onHistory}>Historial</ActionBtn>
      <ActionBtn onClick={p.onDelete}>Eliminar</ActionBtn>
    </div>
  );
}

export function PricingRangeCard({
  range,
  onEdit,
  onHistory,
  onDelete,
}: Props): ReactNode {
  return (
    <Card>
      <CardHeader range={range} />
      <div className="mt-md space-y-px">
        {range.tiers.map((t) => (
          <TierRowReadonly
            key={t.min}
            tier={t}
            active={false}
            todayCreators={undefined}
          />
        ))}
      </div>
      <CardActions onEdit={onEdit} onHistory={onHistory} onDelete={onDelete} />
    </Card>
  );
}
